/**
 * Where a citation goes — the ONE place the console maps a cited record to a
 * destination.
 *
 * ## Why this returns a target rather than a URL
 *
 * Every console view already receives `hrefForItem` / `onOpenItem` and
 * `hrefForWalk` / `onOpenWalk` on `ConsoleViewProps` (`chrome.tsx:941-970`),
 * bound by the shell to the current URL so a link preserves `?workspace=` and
 * every other URL-state parameter. A module that composed its own query string
 * would be the second place that decides tenancy — precisely what the shell's
 * own header forbids.
 *
 * So this returns WHICH destination and WHICH id, and the view turns that into
 * an href with the props it already has.
 *
 * ## Why an unmappable citation returns null rather than throwing
 *
 * A console pointed at a newer backend can receive a `recordKind` it has never
 * heard of. The chip still renders — the reader should still see that the
 * sentence was cited — but it renders inert. A thrown error would take the
 * whole answer off screen because one reference was ahead of this deployment.
 */

export interface CitationTargetInput {
  recordKind: string;
  recordId: string;
  visitId?: string;
}

export interface CitationTarget {
  target: "item" | "walk";
  id: string;
}

const ITEM_KINDS = new Set(["log", "observation", "task", "punch"]);

export function citationTarget(
  reference: CitationTargetInput,
): CitationTarget | null {
  if (reference.recordKind === "walk") {
    // The walk, never `recordId` — a `walk` citation may be a transcript
    // chunk, a summary or a note, and none of those is a console record.
    return reference.visitId ? { target: "walk", id: reference.visitId } : null;
  }
  if (ITEM_KINDS.has(reference.recordKind)) {
    return reference.recordId ? { target: "item", id: reference.recordId } : null;
  }
  return null;
}
